import { useMemo } from "react";
import { t } from "ttag";
import * as Yup from "yup";

import { FormInput } from "metabase/common/components/FormInput";
import { Form, FormProvider, FormSubmitButton } from "metabase/forms";
import type { InviteInfo, UserInfo } from "metabase/redux/store";
import * as Errors from "metabase/utils/errors";

import S from "./InviteUserForm.module.css";

const INVITE_USER_SCHEMA = Yup.object({
  first_name: Yup.string().nullable().default(null).max(100, Errors.maxLength),
  last_name: Yup.string().nullable().default(null).max(100, Errors.maxLength),
  email: Yup.string()
    .default("")
    .required(Errors.required)
    .email(Errors.email)
    .notOneOf(
      [Yup.ref("$email")],
      // eslint-disable-next-line ttag/no-module-declaration -- see metabase#55045
      t`must be different from the email address you used in setup`,
    ),
});

interface InviteUserFormProps {
  user?: UserInfo;
  invite?: InviteInfo;
  onSubmit: (invite: InviteInfo) => void;
}

export const InviteUserForm = ({
  user,
  invite,
  onSubmit,
}: InviteUserFormProps): JSX.Element => {
  const initialValues = useMemo(() => {
    return invite ?? INVITE_USER_SCHEMA.getDefault();
  }, [invite]);

  return (
    <FormProvider
      initialValues={initialValues}
      validationSchema={INVITE_USER_SCHEMA}
      validationContext={user}
      onSubmit={onSubmit}
    >
      <Form data-testid="invite-user-form">
        <div className={S.UserFieldGroup}>
          <FormInput
            name="first_name"
            title={t`First name`}
            placeholder={t`Johnny`}
            nullable
            autoComplete="given-name"
            autoFocus
          />
          <FormInput
            name="last_name"
            title={t`Last name`}
            placeholder={t`Appleseed`}
            nullable
            autoComplete="family-name"
          />
        </div>
        <FormInput
          name="email"
          title={t`Email`}
          placeholder={"nicetoseeyou@email.com"}
          autoComplete="email"
        />
        <FormSubmitButton label={t`Send invitation`} variant="filled" />
      </Form>
    </FormProvider>
  );
};
